/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IRefinerItemData, RefinerItemType } from '@msdyn365-commerce/components';
import { ChannelIdentity, SalesStatus } from '@msdyn365-commerce/retail-proxy';
import { ICustomOrderHistoryResources } from '../custom-order-history.props.autogenerated';
import { getOrderStatusLabel } from './get-sales-order';
import { DateRefinerOptionItem, IDateRange, OrderStatusRefinerOptionItem, RefinerSourceType } from './refiner-option';

export function mapChannelIdentityIntoIRefinerItemData(channelIdentity: ChannelIdentity): IRefinerItemData {
    return {
        sourceTypeValue: RefinerSourceType.Origin,
        label: channelIdentity.Name ?? channelIdentity.RecordId.toString(),
        value: channelIdentity.RecordId.toString(),
        refinerItemId: channelIdentity.RecordId,
        isChecked: false,
        itemType: RefinerItemType.Check
    };
}

export function mapDateRefinerOptionIntoIRefinerItemData(
    dateRefinerOptionItem: DateRefinerOptionItem,
    resources: ICustomOrderHistoryResources
): IRefinerItemData {
    let itemData: IRefinerItemData;
    switch (dateRefinerOptionItem) {
        case DateRefinerOptionItem.LastSevenDays:
            itemData = {
                label: resources.lastSevenDaysCreatedDateRefinerItemLabel,
                itemType: RefinerItemType.Check
            } as IRefinerItemData;
            break;
        case DateRefinerOptionItem.LastThirtyDays:
            itemData = {
                label: resources.lastThirtyDaysCreatedDateRefinerItemLabel,
                itemType: RefinerItemType.Check
            } as IRefinerItemData;
            break;
        case DateRefinerOptionItem.LastThreeMonths:
            itemData = {
                label: resources.lastThreeMonthsCreatedDateRefinerItemLabel,
                itemType: RefinerItemType.Check
            } as IRefinerItemData;
            break;
        case DateRefinerOptionItem.LastSixMonths:
            itemData = {
                label: resources.lastSixMonthsCreatedDateRefinerItemLabel,
                itemType: RefinerItemType.Check
            } as IRefinerItemData;
            break;
        case DateRefinerOptionItem.Customized:
            itemData = {
                label: resources.customizedCreatedDateRefinerItemLabel,
                minLabel: resources.customizedStartDateRefinerItemLabel,
                maxLabel: resources.customizedEndDateRefinerItemLabel,
                itemType: RefinerItemType.DateRange
            } as IRefinerItemData;
            break;
        default:
            throw new Error(`Invalid date refiner option: ${dateRefinerOptionItem}`);
    }

    return {
        ...itemData,
        sourceTypeValue: RefinerSourceType.CreatedDate,
        value: dateRefinerOptionItem.toString(),
        refinerItemId: dateRefinerOptionItem,
        isChecked: false
    } as IRefinerItemData;
}

export function mapIRefinerItemDataIntoDateRefinerOptionItem(refinerItemData?: IRefinerItemData): DateRefinerOptionItem | undefined {
    if (!refinerItemData) {
        return;
    }

    const value = Number(refinerItemData.value);
    if (isNaN(value)) {
        return;
    }

    switch (value) {
        case DateRefinerOptionItem.LastSevenDays:
        case DateRefinerOptionItem.LastThirtyDays:
        case DateRefinerOptionItem.LastThreeMonths:
        case DateRefinerOptionItem.LastSixMonths:
        case DateRefinerOptionItem.Customized:
            return value;
        default:
            return;
    }
}

export function getDateRange(dateRefinerOptionItem: DateRefinerOptionItem): IDateRange {
    switch (dateRefinerOptionItem) {
        case DateRefinerOptionItem.LastSevenDays:
            return {
                startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
                endDate: new Date()
            } as IDateRange;
        case DateRefinerOptionItem.LastThirtyDays:
            return {
                startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
                endDate: new Date()
            } as IDateRange;
        case DateRefinerOptionItem.LastThreeMonths:
            return {
                startDate: new Date(Date.now() - 3 * 30 * 24 * 60 * 60 * 1000),
                endDate: new Date()
            } as IDateRange;
        case DateRefinerOptionItem.LastSixMonths:
            return {
                startDate: new Date(Date.now() - 6 * 30 * 24 * 60 * 60 * 1000),
                endDate: new Date()
            } as IDateRange;
        default:
            return {} as IDateRange;
    }
}

export function getDateFromString(dateString?: string): Date | undefined {
    if (!dateString) {
        return undefined;
    }

    const parts: string[] = dateString.split('-');
    if (parts.length !== 3) {
        return undefined;
    }

    const now = new Date();
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), now.getHours(), now.getMinutes(), now.getSeconds());
}

export function mapOrderStatusRefinerOptionIntoIRefinerItemData(
    orderStatusRefinerOptionItem: OrderStatusRefinerOptionItem,
    resources: ICustomOrderHistoryResources
): IRefinerItemData {
    let itemData: IRefinerItemData;
    switch (orderStatusRefinerOptionItem) {
        case OrderStatusRefinerOptionItem.Created:
        case OrderStatusRefinerOptionItem.Delivered:
        case OrderStatusRefinerOptionItem.Invoiced:
        case OrderStatusRefinerOptionItem.Canceled:
            itemData = {
                label: getOrderStatusRefinerOptionItemLabel(orderStatusRefinerOptionItem, resources)
            } as IRefinerItemData;
            break;
        default:
            throw new Error(`Invalid order status refiner option: ${orderStatusRefinerOptionItem}`);
    }

    return {
        ...itemData,
        sourceTypeValue: RefinerSourceType.OrderStatus,
        value: orderStatusRefinerOptionItem.toString(),
        refinerItemId: orderStatusRefinerOptionItem,
        isChecked: false,
        itemType: RefinerItemType.Check
    } as IRefinerItemData;
}

function getOrderStatusRefinerOptionItemLabel(
    orderStatusRefinerOptionItem: OrderStatusRefinerOptionItem,
    resources: ICustomOrderHistoryResources
): string {
    return getOrderStatusLabel(mapOrderStatusRefinerOptionItemIntoSalesStatus(orderStatusRefinerOptionItem), resources);
}

export function mapIRefinerItemDataIntoOrderStatusRefinerOptionItem(
    refinerItemData?: IRefinerItemData
): OrderStatusRefinerOptionItem | undefined {
    if (!refinerItemData) {
        return;
    }

    const value = Number(refinerItemData.value);
    if (isNaN(value)) {
        return;
    }

    switch (value) {
        case OrderStatusRefinerOptionItem.Created:
        case OrderStatusRefinerOptionItem.Delivered:
        case OrderStatusRefinerOptionItem.Invoiced:
        case OrderStatusRefinerOptionItem.Canceled:
            return value as OrderStatusRefinerOptionItem;
        default:
            return;
    }
}

export function mapOrderStatusRefinerOptionItemIntoSalesStatus(
    orderStatusRefinerOptionItem?: OrderStatusRefinerOptionItem
): SalesStatus | undefined {
    switch (orderStatusRefinerOptionItem) {
        case OrderStatusRefinerOptionItem.Created:
            return SalesStatus.Created;
        case OrderStatusRefinerOptionItem.Delivered:
            return SalesStatus.Delivered;
        case OrderStatusRefinerOptionItem.Invoiced:
            return SalesStatus.Invoiced;
        case OrderStatusRefinerOptionItem.Canceled:
            return SalesStatus.Canceled;
        default:
            return;
    }
}
